// Zeilen für die CSV-Datei einer Aktivitäten-Liste (die geladenen Zeilen), rein (mit node --test prüfbar, deshalb nur
// Typ-Importe und die Dateien dieses Ordners).
import type { AktivitaetenArt, AktivitaetZeile, AnrufDetails, CustomDetails, MailDetails, MeetingDetails, NachrichtDetails } from "../api/aktivitaeten";
import { ergebnisText, statusText } from "./texte";
import { berlinTeile, datumText, gueltig } from "./zeiten";

export interface FeldSpalte {
  id: string;
  name: string;
}

/** Wert eines Feldes als Text (Auswahl, Datum, Nutzername …); die Oberfläche übergibt `feldText` mit den Stammdaten. */
export type FeldWertText = (feldId: string, wert: unknown) => string;

function zeitText(iso: string | null): string {
  if (!gueltig(iso)) return "";
  const t = berlinTeile(iso);
  return `${datumText(t.datum)} ${t.zeit}`;
}

const richtungText = (r: string | null | undefined) => (r === "ausgehend" ? "Ausgehend" : r === "eingehend" ? "Eingehend" : "");

export function csvKopf(art: AktivitaetenArt, felder: readonly FeldSpalte[] = []): string[] {
  switch (art) {
    case "anrufe":
      return ["Datum", "Lead", "Kontakt", "Richtung", "Ergebnis", "Dauer (Sekunden)", "Mitarbeiter", "Notiz", "Telefon"];
    case "emails":
      return ["Datum", "Lead", "Kontakt", "Richtung", "Betreff", "Von", "An", "Mitarbeiter", "Anhänge", "Text"];
    case "sms":
    case "whatsapp":
      return ["Datum", "Lead", "Kontakt", "Richtung", "Nachricht", "Telefon", "Mitarbeiter"];
    case "meetings":
      return ["Beginn", "Lead", "Kontakt", "Titel", "Status", "Ort", "Mitarbeiter", "Notiz"];
    case "notizen":
      return ["Datum", "Lead", "Kontakt", "Titel", "Notiz", "Mitarbeiter"];
    case "custom":
      return ["Datum", "Lead", "Kontakt", "Mitarbeiter", "Status", ...felder.map((f) => f.name)];
  }
}

export function csvZeile(art: AktivitaetenArt, z: AktivitaetZeile, felder: readonly FeldSpalte[] = [], feldWert: FeldWertText = (_i, w) => String(w ?? "")): string[] {
  const kopf = [zeitText(z.activity_at), z.lead_name ?? "", z.contact_name ?? ""];
  switch (art) {
    case "anrufe": {
      const d = z.details as Partial<AnrufDetails>;
      return [...kopf, richtungText(d.richtung), ergebnisText(d.ergebnis), d.dauer === null || d.dauer === undefined ? "" : String(d.dauer), z.user_name ?? "", z.text ?? "", d.telefon ?? ""];
    }
    case "emails": {
      const d = z.details as Partial<MailDetails>;
      return [...kopf, richtungText(d.richtung), z.titel ?? "", d.von ?? "", (d.an ?? []).join("; "), z.user_name ?? "", String(d.anhaenge ?? 0), z.text ?? ""];
    }
    case "sms":
    case "whatsapp": {
      const d = z.details as Partial<NachrichtDetails>;
      return [...kopf, richtungText(d.richtung), z.text ?? "", d.telefon ?? "", z.user_name ?? ""];
    }
    case "meetings": {
      const d = z.details as Partial<MeetingDetails>;
      return [...kopf, z.titel ?? "", statusText(d.status), d.ort ?? "", z.user_name ?? "", z.text ?? ""];
    }
    case "notizen":
      return [...kopf, z.titel ?? "", z.text ?? "", z.user_name ?? ""];
    case "custom": {
      const d = z.details as Partial<CustomDetails>;
      const werte = d.felder ?? {};
      return [...kopf, z.user_name ?? "", statusText(d.status), ...felder.map((f) => (f.id in werte ? feldWert(f.id, werte[f.id]) : ""))];
    }
  }
}

export function csvZeilen(art: AktivitaetenArt, zeilen: readonly AktivitaetZeile[], felder: readonly FeldSpalte[] = [], feldWert?: FeldWertText): string[][] {
  return [csvKopf(art, felder), ...zeilen.map((z) => csvZeile(art, z, felder, feldWert))];
}
